<script setup>
import Card from '@/components/shared/page/Card.vue'
import Markdown from '@/components/shared/transform/Markdown.vue'
import Heading from '@/components/shared/page/Heading.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import UnreadBadge from '@/components/shared/core/UnreadBadge.vue'
import DateTimeRelative from '@/components/shared/transform/DateTimeRelative.vue'
import EnrollmentProgress from '@/components/enrollments/EnrollmentProgress.vue'
import EnrollmentStatus from '@/components/enrollments/EnrollmentStatus.vue'
import {Enrollment} from '@/models/enrollment.model.js'
import {useEnrollmentStatus} from '@/composables/use-enrollment-status.js'
import {toRefs} from 'vue'

//Emits and props
defineEmits([
  'click',
])
const props = defineProps({
  enrollment: {
    type: Enrollment,
    required: true,
  },
  isUnread: {
    type: Boolean,
    default: false,
  },
})

//Refs
const {enrollment} = toRefs(props)
const {showProgress} = useEnrollmentStatus(enrollment)
</script>

<template>
  <Card
    is-clickable
    tabindex="0"
    :class="$style.card"
    @click="$emit('click', $event)"
    @keydown.enter="$emit('click', $event)"
    @keydown.space="$emit('click', $event)"
  >
    <div :class="$style.header">
      <div :class="$style.headingContainer">
        <Heading
          size="s"
          :has-margin="false"
          :class="$style.heading"
        >
          {{ enrollment.displayName }}
        </Heading>
      </div>
      <small text-muted>
        <DateTimeRelative :date="enrollment.lastMessageSentAt" />
      </small>
      <Icon
        size="s"
        icon="chevron_right"
        color="muted"
        :class="$style.chevron"
        is-clickable
      />
    </div>
    <div :class="$style.content">
      <Markdown
        :content="enrollment.lastMessagePreview || 'No messages yet'"
        strip-tags
        :class="$style.message"
      />
      <UnreadBadge v-if="isUnread && !enrollment.hasEnded" />
    </div>
    <div :class="$style.progress">
      <EnrollmentStatus :status="enrollment.status" />
      <EnrollmentProgress
        v-if="showProgress"
        :num-steps="enrollment.numSteps"
        :num-completed="enrollment.numCompleted"
      />
    </div>
  </Card>
</template>

<style module lang="scss">
.card {
  gap: 0.5rem;
}
.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}
.headingContainer {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.heading {
  flex: 1;
  display: block;
  display: -webkit-box;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: var(--line-height-text);
}
.message {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-muted);
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}
.progress {
  display: flex;
  gap: 0.5rem;
}
.chevron {
  margin-right: -5px;
}
</style>
